import { initCommerceApiWithGlobals } from 'common-api';
import { logger, useAdobeBasketDetails, isBrowser } from 'common-ui';
import type { CartItemProps } from 'tsconfig/types';
import { useAuthStore } from '../../../stores/authStore';
import { useCartStore } from '../../../stores/cartStore';
import { useNotificationsStore } from '../../../stores/notificationsStore';
import { commerceApiExtraConfig } from '../../../helpers/commerceApiConfig';
import { cartNotification } from '../../../helpers/cartNotification';
import { commonHandlerForWebApiRequestError } from '../../../helpers/commonHandlerForWebApiRequestError';
import { getProducts } from '../../../helpers/getProducts';
import { getCurrentCartIdFromCookie } from '../../../helpers/utils';
import useWaitForCartToLoad from './useWaitForCartToLoad';
import { reloadAuthToken } from '../../../helpers/auth/reloadAuthToken';

export function useAddToCartWithWebApi(): (items: CartItemProps[]) => Promise<void> {
  const waitForCartToLoad = useWaitForCartToLoad();
  const adobeBasketDetails = useAdobeBasketDetails();

  const prepareCartItems = (cartItems: CartItemProps[]) =>
    cartItems.map(({ itemCode, quantity, itemType, giftVoucherDetails, upsellOfferDetails }) => ({
      itemCode: String(itemCode),
      quantity: Number(quantity),
      ...(itemType && { itemType }),
      ...(giftVoucherDetails && { giftVoucherDetails }),
      ...(upsellOfferDetails
        ? {
            classificationDetailsInput: {
              upsell: { type: upsellOfferDetails.type.toUpperCase(), rule: upsellOfferDetails?.rule || '' },
            },
          }
        : {}),
    }));

  const updateCartData = async (newCartData: any) => {
    const productArray = await getProducts(newCartData);
    useCartStore.getState().updateCart(prevState => ({
      ...prevState,
      cartIsLoaded: true,
      cartItems: {
        ...prevState.cartItems,
        ...newCartData,
        ...(productArray?.length ? { lineItems: productArray } : {}),
      },
    }));
  };

  const showNotification = (cartItems: CartItemProps[]) => {
    const minicartPayload =
      cartItems.length > 1
        ? cartItems.find(item => item.upsellOfferDetails?.type.toLowerCase() !== 'wineplan')
        : cartItems[0];

    if (!minicartPayload) {
      logger.log('No valid item found for notification');
      return;
    }

    const { itemCode, quantity } = minicartPayload;
    const notification = cartNotification(itemCode, quantity.toString());
    useNotificationsStore.setState({ notifications: [notification] });
  };

  const handleAdobeBasketDetails = () => {
    const locationHref = isBrowser() ? window.location.href : '/';
    if (!locationHref.includes('/cart')) {
      adobeBasketDetails();
    }
  };

  const addItemsToCart = async (cartItems: CartItemProps[], authToken: string) => {
    const commerceApi = initCommerceApiWithGlobals(commerceApiExtraConfig(authToken));
    const { cartId, cartVersion } = getCurrentCartIdFromCookie();
    const items = prepareCartItems(cartItems);

    return commerceApi.CartPreferences.addItemToCart({
      cartId,
      data: { version: cartVersion, items },
    });
  };

  const doAddToCart = async (cartItems: CartItemProps[]) => {
    if (!isBrowser()) {
      logger.log('useAddToCartWithWebApi skipping as not in browser');
      return;
    }

    let authAtomState = useAuthStore.getState().auth;

    if (!authAtomState?.publicCsrf) {
      authAtomState = (await reloadAuthToken()) || authAtomState;
    }

    const newCartData = await addItemsToCart(cartItems, authAtomState.publicCsrf);

    await updateCartData(newCartData);

    showNotification(cartItems);

    handleAdobeBasketDetails();
  };

  return async (cartItems: CartItemProps[]) => {
    try {
      await waitForCartToLoad();
      await doAddToCart(cartItems);
    } catch (error: any) {
      logger.log('Error occurred while adding to cart:', error);
      return commonHandlerForWebApiRequestError(error, () => doAddToCart(cartItems));
    }
  };
}
